/* eslint-disable no-param-reassign */
import {
  DataGridProps,
  GridCellEditStopParams,
  GridColDef,
  GridPreProcessEditCellProps,
  GridValidRowModel,
} from '@mui/x-data-grid';
import React from 'react';
import {
  StyledGrid,
  CellWithValidationTooltip,
  StringEditWithValidationProps,
} from './components';

export interface GridColDefWithValidation<
  R extends GridValidRowModel = any,
  V = any,
  F = V
> extends GridColDef<R, V, F> {
  validator?: (cellVal: V) => string | true;
}

export interface RowWithErrors extends Record<string, any> {
  errors: Partial<Record<string, string>>;
  blurredFields: Set<string>;
}

export function resetRowErrorState(row: RowWithErrors) {
  row.errors = {};
  row.blurredFields.clear();
}

export function validateRow<
  R extends RowWithErrors,
  C extends GridColDefWithValidation<R>
>(row: R, cols: C[], shouldValidateRow: (r: R) => boolean = () => true) {
  let noErrors = true;
  if (!shouldValidateRow(row)) {
    return noErrors;
  }

  cols.forEach((col) => {
    if (!col.validator) {
      return;
    }

    const rv = col.validator(row[col.field]);
    const hasError = typeof rv === 'string' && rv.length > 0;
    if (hasError) {
      row.errors[col.field] = rv as string;
      noErrors = false;
    } else {
      delete row.errors[col.field];
    }
  });

  return noErrors;
}

export function validateAllRows<
  R extends RowWithErrors,
  C extends GridColDefWithValidation<R>
>(rows: R[], cols: C[], shouldValidateRow: (r: R) => boolean) {
  let noErrors = true;
  rows.forEach((row) => {
    if (!validateRow(row, cols, shouldValidateRow)) {
      noErrors = false;
    }
  });
  return noErrors;
}

// FIXME: We want to use `preProcessEditCellProps` to validate as the user types in a cell, however
// there currently is a bug that gives some undesirable behavior:
// https://github.com/mui/mui-x/issues/5218
// Once that is resolved, we will want to use this preprocessEditCellProps function along with
// renderStringEditWithValidation in our column definition
export function getStringPreprocessor(col: GridColDefWithValidation) {
  return (params: GridPreProcessEditCellProps) => {
    const row = params.row as RowWithErrors;
    // if the cell has never been blurred and does not have an error from any other
    // source, then we do not want to continuously validate as the user types.
    if (!row.blurredFields.has(col.field) && !row.errors[col.field]) {
      return params.props;
    }

    let errorMsg = '';
    if (col.validator) {
      const rv = col.validator(params.props.value);
      if (rv !== true) {
        errorMsg = rv;
      }
    }

    return {
      ...params.props,
      // currently only string fields can accept this error prop, and not
      // other cell types like single select
      errorMsg,
    } as StringEditWithValidationProps;
  };
}

export function mkCols<T extends GridColDefWithValidation>(...cols: T[]): T[] {
  return cols.map((col) => {
    const colDefaults: Partial<GridColDef> = {};

    // action cell type does some stuff with focus
    if (col.type !== 'actions') {
      colDefaults.renderCell = CellWithValidationTooltip;
    }

    return {
      ...colDefaults,
      ...col,
    };
  });
}

function editStopHandler(params: GridCellEditStopParams<any, RowWithErrors>) {
  // if we were able to stop editing, then that means the cell must have valid
  // content. Clear any errors that may have been set programatically earlier
  // eslint-disable-next-line no-param-reassign
  delete params.row.errors[params.field];
  params.row.blurredFields.add(params.field);
}

const DataGridWrapper = <R extends RowWithErrors>(props: DataGridProps<R>) => {
  return (
    <StyledGrid
      hideFooterSelectedRowCount
      showColumnRightBorder
      showCellRightBorder
      rowHeight={44}
      headerHeight={27}
      experimentalFeatures={{ newEditingApi: true }}
      onCellEditStop={editStopHandler}
      onCellFocusOut={(params) => {
        params.row.blurredFields.add(params.field);
      }}
      rowsPerPageOptions={[25, 50, 100]}
      initialState={{
        pagination: {
          pageSize: 25,
        },
      }}
      {...props}
      components={{
        ColumnResizeIcon: () => null,
        ...props.components,
      }}
    />
  );
};

export { DataGridWrapper as DataGrid };
